import {
  Building2, Warehouse, Castle, House, Hotel, Heart, Gem, Trees, Wine, PoundSterling,
  Camera, Flower2, Music2, Video, Utensils, PartyPopper, CakeSlice, Shirt, Circle,
  ClipboardList, BookOpen, Brush, Mail, Car, Lamp, Tent, ListChecks, Wallet,
  Users, PanelsTopLeft, Armchair, CalendarDays, NotebookPen, Palette, FolderOpen, Search,
  createLucideIcon, type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { PlanningIcon, type PlanningIconName } from "./PlanningIcons";

export type WeddingIconName =
  | "venue" | "barn" | "castle" | "house" | "hotel" | "intimate" | "luxury" | "outdoor" | "pub" | "affordable"
  | "camera" | "flower" | "music" | "video" | "catering" | "party" | "cake" | "dress" | "suit" | "rings"
  | "planner" | "celebrant" | "beauty" | "stationery" | "car" | "decor" | "marquee"
  | "checklist" | "budget" | "guests" | "website" | "seating" | "heart" | "calendar" | "notes" | "palette" | "portal" | "search";

const Dress = createLucideIcon("WeddingDress", [
  ["path", { d: "M9 3v3L7.5 9l2 3L5 21h14l-4.5-9 2-3L15 6V3", key: "dress" }],
  ["path", { d: "M9 6h6M9.5 12h5", key: "waist" }],
]);
const Rings = createLucideIcon("WeddingRings", [
  ["circle", { cx: "9", cy: "15", r: "5", key: "left" }],
  ["circle", { cx: "16", cy: "15", r: "5", key: "right" }],
  ["path", { d: "m7 6 2-3 2 3-2 3Z", key: "stone" }],
]);

const icons: Record<WeddingIconName, LucideIcon> = {
  venue: Building2, barn: Warehouse, castle: Castle, house: House, hotel: Hotel,
  intimate: Heart, luxury: Gem, outdoor: Trees, pub: Wine, affordable: PoundSterling,
  camera: Camera, flower: Flower2, music: Music2, video: Video, catering: Utensils,
  party: PartyPopper, cake: CakeSlice, dress: Dress, suit: Shirt, rings: Rings,
  planner: ClipboardList, celebrant: BookOpen, beauty: Brush, stationery: Mail,
  car: Car, decor: Lamp, marquee: Tent, checklist: ListChecks, budget: Wallet,
  guests: Users, website: PanelsTopLeft, seating: Armchair, heart: Heart,
  calendar: CalendarDays, notes: NotebookPen, palette: Palette, portal: FolderOpen, search: Search,
};

/** One optical weight and grid across navigation, categories and planning tools. */
export function WeddingIcon({ name, className, strokeWidth = 1.75, title }: {
  name: WeddingIconName; className?: string; strokeWidth?: number; title?: string;
}) {
  const Icon = icons[name];
  return <Icon className={cn("h-6 w-6 shrink-0", className)} strokeWidth={strokeWidth}
    aria-hidden={title ? undefined : true} aria-label={title} role={title ? "img" : undefined} />;
}

/** Kept as a compatible API for existing category and tool consumers. */
export function IconBlob({ name, size = "md", className }: {
  name: WeddingIconName; index?: number; size?: "sm" | "md" | "lg"; className?: string;
}) {
  const box = size === "lg" ? "h-12 w-12" : size === "sm" ? "h-8 w-8" : "h-10 w-10";
  const icon = size === "lg" ? "h-7 w-7" : size === "sm" ? "h-5 w-5" : "h-6 w-6";
  const planning: Partial<Record<WeddingIconName, PlanningIconName>> = { checklist: "checklist", budget: "budget", guests: "guests", website: "website", seating: "seating", calendar: "events", notes: "notes", palette: "moodboard", rings: "wedding", planner: "build" };
  const pictogram = planning[name];
  return <span className={cn("inline-flex shrink-0 items-center justify-center text-foreground", box, className)}>
    {pictogram ? <PlanningIcon name={pictogram} className={icon} /> : <WeddingIcon name={name} className={icon} />}
  </span>;
}
